"use client";

import { Calendar, User, ArrowLeft, ArrowRight, Clock } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-12">
        <div className="space-y-6">
          <div className="h-8 w-32 rounded-md bg-muted/50 animate-pulse" />
          <div className="h-12 w-3/4 rounded-md bg-muted/50 animate-pulse" />
          <div className="h-6 w-1/2 rounded-md bg-muted/50 animate-pulse" />
          <div className="h-96 w-full rounded-lg bg-muted/50 animate-pulse" />
          <div className="space-y-3">
            <div className="h-4 w-full rounded-md bg-muted/50 animate-pulse" />
            <div className="h-4 w-full rounded-md bg-muted/50 animate-pulse" />
            <div className="h-4 w-5/6 rounded-md bg-muted/50 animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (error || posts.length === 0) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-24 text-center">
        <h2 className="text-2xl font-semibold text-foreground font-default">Update not found</h2>
        <p className="mt-3 text-muted-foreground">The classroom update you are looking for could not be loaded.</p>
        <Link to="/Class2AHubPage" className="mt-6 inline-block">
          <Button variant="outline" className="rounded-lg">
            <ArrowLeft className="size-4" />
            Back to Class Hub
          </Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];
  const formattedDate = post.date
    ? new Date(post.date).toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

  return (
    <article className="mx-auto max-w-4xl px-4 sm:px-6 py-10 md:py-16">
      {}
      {post.categories && post.categories.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-5">
          {post.categories.map((cat, i) => (
            <Link
              key={cat.id}
              to={cat.link || "#"}
              data-index={i}
              className="text-foreground inline-flex items-center rounded-lg bg-accent px-3 py-1 text-xs font-medium uppercase tracking-wider hover:bg-accent/80 transition-colors"
            >
              {cat.name}
            </Link>
          ))}
        </div>
      )}

      {}
      <h1
        className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground font-default leading-tight"
        dangerouslySetInnerHTML={{ __html: post.title }}
      />

      {}
      <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-muted-foreground">
        {post.author && (
          <div className="flex items-center gap-2">
            <Avatar className="size-8 rounded-lg">
              {post.author.avatar && post.author.avatar["48"] && (
                <AvatarImage src={post.author.avatar["48"]} alt={post.author.name} />
              )}
              <AvatarFallback className="rounded-lg bg-muted text-xs">
                {post.author.name?.charAt(0) || "A"}
              </AvatarFallback>
            </Avatar>
            <span className="font-medium text-foreground">{post.author.name}</span>
          </div>
        )}
        {formattedDate && (
          <div className="flex items-center gap-2">
            <Calendar className="size-4" />
            <span>{formattedDate}</span>
          </div>
        )}
        <div className="flex items-center gap-2">
          <Clock className="size-4" />
          <span>Class Update</span>
        </div>
      </div>

      {}
      {post.featuredImage && (
        <div className="mt-10 overflow-hidden rounded-lg shadow-lg">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="w-full h-auto object-cover aspect-[16/9]"
            loading="lazy"
            decoding="async"
          />
        </div>
      )}

      {}
      {post.excerpt && (
        <div
          className="mt-10 text-lg md:text-xl text-muted-foreground leading-relaxed italic border-l-4 border-primary pl-6 font-serif"
          dangerouslySetInnerHTML={{ __html: post.excerpt }}
        />
      )}

      {}
      {post.content && (
        <div
          className="mt-10 prose prose-lg max-w-none text-foreground leading-relaxed [&_h1]:font-default [&_h1]:text-foreground [&_h1]:text-4xl [&_h1]:font-bold [&_h1]:mt-12 [&_h1]:mb-4 [&_h2]:font-default [&_h2]:text-foreground [&_h2]:text-3xl [&_h2]:font-bold [&_h2]:mt-10 [&_h2]:mb-4 [&_h3]:font-default [&_h3]:text-foreground [&_h3]:text-2xl [&_h3]:font-semibold [&_h3]:mt-8 [&_h3]:mb-3 [&_h4]:font-default [&_h4]:text-foreground [&_h4]:text-xl [&_h4]:font-semibold [&_h4]:mt-6 [&_h4]:mb-2 [&_p]:text-foreground [&_p]:my-5 [&_p]:leading-relaxed [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-4 hover:[&_a]:text-primary/80 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-5 [&_ul]:space-y-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-5 [&_ol]:space-y-2 [&_li]:text-foreground [&_blockquote]:border-l-4 [&_blockquote]:border-primary [&_blockquote]:pl-6 [&_blockquote]:italic [&_blockquote]:text-muted-foreground [&_blockquote]:my-6 [&_img]:rounded-lg [&_img]:my-8 [&_img]:shadow-md [&_code]:bg-muted [&_code]:px-2 [&_code]:py-0.5 [&_code]:rounded-lg [&_code]:text-sm [&_code]:font-mono [&_pre]:bg-muted [&_pre]:p-4 [&_pre]:rounded-lg [&_pre]:overflow-x-auto [&_pre]:my-6 [&_strong]:text-foreground [&_strong]:font-semibold"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
      )}

      {}
      {post.tags && post.tags.length > 0 && (
        <div className="mt-12 flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground mr-2">Topics:</span>
          {post.tags.map((tag, i) => (
            <Link
              key={tag.id}
              to={tag.link || "#"}
              data-index={i}
              className="text-foreground inline-flex items-center rounded-lg border border-border bg-background px-3 py-1 text-xs font-medium hover:bg-muted transition-colors"
            >
              #{tag.name}
            </Link>
          ))}
        </div>
      )}

      <Separator className="my-12" />

      {}
      {post.author && (
        <Card className="bg-card border border-border shadow-md rounded-lg">
          <CardContent className="p-6 md:p-8">
            <div className="flex flex-col sm:flex-row items-start gap-5">
              <Avatar className="size-20 rounded-lg shrink-0">
                {post.author.avatar && post.author.avatar["96"] && (
                  <AvatarImage src={post.author.avatar["96"]} alt={post.author.name} />
                )}
                <AvatarFallback className="rounded-lg bg-primary text-primary-foreground text-2xl font-semibold">
                  {post.author.name?.charAt(0) || "A"}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                  <User className="size-4" />
                  <span>Teacher</span>
                </div>
                <h3 className="text-xl font-semibold text-foreground font-default">
                  {post.author.name}
                </h3>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                  Class 2A Lead Teacher. Dedicated to creating a fun, inclusive, and creative learning environment for all our students.
                </p>
                {post.author.link && (
                  <Link to={post.author.link} className="mt-4 inline-block">
                    <Button variant="outline" size="sm" className="rounded-lg">
                      View all updates
                      <ArrowRight className="size-4" />
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </article>
  );
}

function RelatedPostsConsumer() {
  const { posts, loading } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-72 rounded-lg bg-muted/50 animate-pulse" />
        ))}
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <p className="text-muted-foreground text-center py-8">No other classroom updates at this time.</p>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {posts.slice(0, 3).map((post, i) => (
        <Card
          key={post.id}
          data-index={i}
          className="bg-card border border-border shadow-md hover:shadow-lg transition-shadow rounded-lg overflow-hidden flex flex-col"
        >
          {post.featuredImage && (
            <Link to={post.link || "#"} className="block overflow-hidden">
              <img
                src={post.featuredImage}
                alt={post.title}
                className="w-full h-48 object-cover hover:scale-105 transition-transform duration-300"
                loading="lazy"
                decoding="async"
              />
            </Link>
          )}
          <CardContent className="p-5 flex flex-col flex-1">
            {post.categories && post.categories.length > 0 && (
              <span className="inline-block text-xs font-medium uppercase tracking-wider text-primary-foreground bg-primary px-2 py-0.5 rounded-lg w-fit mb-3">
                {post.categories[0].name}
              </span>
            )}
            <Link to={post.link || "#"} className="group">
              <h3
                className="text-lg font-semibold text-foreground font-default leading-snug group-hover:text-primary transition-colors line-clamp-2"
                dangerouslySetInnerHTML={{ __html: post.title }}
              />
            </Link>
            {post.excerpt && (
              <div
                className="mt-3 text-sm text-muted-foreground line-clamp-3 flex-1"
                dangerouslySetInnerHTML={{ __html: post.excerpt }}
              />
            )}
            <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
              {post.date && (
                <span>
                  {new Date(post.date).toLocaleDateString(undefined, {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                </span>
              )}
              <Link to={post.link || "#"} className="text-primary font-medium hover:underline inline-flex items-center gap-1">
                Read
                <ArrowRight className="size-3" />
              </Link>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export default function SinglePostTemplateSection() {
  const currentWPQueryParams = (window as any).wvcClient.getCurrentWPQueryParams();
  const singlePostQuery = new WP_Query(currentWPQueryParams);
  const relatedPostsQuery = new WP_Query({ post_type: "post", per_page: 3 });

  return (
    <section data-section-id="588"
      id="single-post-template-section"
      className="bg-background text-foreground font-default w-full"
    >
      <PostsProvider wp_query={singlePostQuery}>
        <SinglePostContent />
      </PostsProvider>

      <div className="mx-auto max-w-6xl px-4 sm:px-6 pb-16 md:pb-24">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <p className="text-sm font-medium uppercase tracking-wider text-primary mb-2">
              More from Class 2A
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground font-default tracking-tight">
              Recent Classroom News
            </h2>
          </div>
          <Link to="/Class2AHubPage" className="hidden sm:inline-flex">
            <Button variant="outline" className="rounded-lg">
              View all updates
              <ArrowRight className="size-4" />
            </Button>
          </Link>
        </div>

        <PostsProvider wp_query={{ post_type: "post", per_page: 3 }}>
          <RelatedPostsConsumer />
        </PostsProvider>
      </div>
    </section>
  );
}